import React, { useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";
import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Toaster } from "react-hot-toast";
import { DataGrid } from "@mui/x-data-grid";
import Chart from "react-apexcharts";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import exportExcel from "utils/excelGenerate";
import { getVisitsStats } from "_controllers/officer";
import { toastMessage } from "utils/toastMessage";
import { useHistory } from "react-router-dom";
import {
  PageHeader,
  StatCard,
  SectionCard,
  EmptyState,
  useChartBase,
  mergeChartOptions,
} from "_shared/ui";

const Main = () => {
  const history = useHistory();

  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [visitData, setVisitData] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [loading, setLoading] = useState(true);

  const columns = useMemo(() => [
    {
      field: "SAP_ID",
      headerName: "SAP ID",
      flex: 1,
    },
    {
      field: "zone",
      headerName: "Zone",
      flex: 1,
    },
    {
      field: "region",
      headerName: "Region",
      flex: 2,
    },
    {
      field: "area",
      headerName: "Area",
      flex: 2,
    },
    {
      field: "planned",
      headerName: "Planned",
      flex: 1,
    },
    {
      field: "unplanned",
      headerName: "Unplanned",
      flex: 1,
    },
    {
      field: "Prospect Visits",
      headerName: "Prospect",
      flex: 1,
    },
    {
      field: "Customer Visits",
      headerName: "Customer",
      flex: 1,
    },
    {
      field: "Influencer Visits",
      headerName: "Influencer",
      flex: 1,
    },
  ]);

  const handleChange = (e) => {
    setSearch(e.target.value);
  };

  useEffect(async () => {
    await getVisitsStats()
      .then((d) => {
        const test = d?.usersWithStats.map((user) => ({
          id: v4(),
          SAP_ID: user.SAP_ID,
          zone: user.zone ? user.zone : "-",
          region: user.regionName ? user.regionName : "-",
          area: user.areaName ? user.areaName : "-",
          territory: user.territoryName ? user.territoryName : "-",
          planned: user?.stats?.planned || 0,
          unplanned: user?.stats?.unplanned || 0,
          "Prospect Visits": user?.prospect?.count || 0,
          "Customer Visits": user?.customer?.count || 0,
          "Influencer Visits": user?.influencer?.count || 0,
        }));
        setVisitData(test);
        setLoading(false);
      })
      .catch((err) => {
        console.log("err", err);
        setLoading(false);
        toastMessage("error fetching data", "error");
      });
  }, []);

  useEffect(() => {
    const res = visitData
      .filter(
        (data) =>
          data?.SAP_ID?.toUpperCase().includes(search.toUpperCase()) ||
          data?.zone?.toUpperCase().includes(search.toUpperCase()) ||
          data?.region?.toUpperCase().includes(search.toUpperCase()) ||
          data?.area?.toUpperCase().includes(search.toUpperCase())
      )
      .map((user) => ({
        id: user?.id,
        SAP_ID: user?.SAP_ID,
        zone: user?.zone ? user.zone : "-",
        region: user?.region ? user.region : "-",
        area: user?.area ? user.area : "-",
        // territory: user.territoryName ? user.territoryName : "-",
        planned: user?.planned || 0,
        unplanned: user?.unplanned || 0,
        "Prospect Visits": user?.["Prospect Visits"] || 0,
        "Customer Visits": user?.["Customer Visits"] || 0,
        "Influencer Visits": user?.["Influencer Visits"] || 0,
      }));
    setSearchData(res);
  }, [search]);

  let fileName = "Visits-Stats";

  const generateExcel = async (e) => {
    e.preventDefault();
    exportExcel(
      visitData.map((x) => ({
        "SAP ID": x?.SAP_ID,
        Zone: x?.zone,
        Region: x?.region,
        Area: x?.area,
        "Planned Visits": x?.planned,
        "Unplanned Visits": x?.unplanned,
        "Prospect Visits": x["Prospect Visits"],
        "Customer Visits": x["Customer Visits"],
        "Influencer Visits": x["Influencer Visits"],
      })),
      fileName
    );
  };

  // Aggregate headline totals across every representative (derived from the
  // data already loaded — no new API calls or fabricated metrics).
  const totals = useMemo(
    () =>
      visitData.reduce(
        (acc, r) => {
          acc.planned += Number(r.planned) || 0;
          acc.unplanned += Number(r.unplanned) || 0;
          acc.prospect += Number(r["Prospect Visits"]) || 0;
          acc.customer += Number(r["Customer Visits"]) || 0;
          acc.influencer += Number(r["Influencer Visits"]) || 0;
          return acc;
        },
        { planned: 0, unplanned: 0, prospect: 0, customer: 0, influencer: 0 }
      ),
    [visitData]
  );
  const totalVisits = totals.prospect + totals.customer + totals.influencer;

  // Theme-aware ApexCharts options (light + dark) via the shared adapter.
  const chartBase = useChartBase();

  const breakdownOptions = mergeChartOptions(chartBase, {
    labels: ["Prospect", "Customer", "Influencer"],
    legend: { position: "bottom" },
    dataLabels: { enabled: true },
    plotOptions: { pie: { donut: { size: "68%" } } },
  });
  const breakdownSeries = [totals.prospect, totals.customer, totals.influencer];

  const categoryOptions = mergeChartOptions(chartBase, {
    chart: { type: "bar" },
    plotOptions: {
      bar: { horizontal: true, distributed: true, borderRadius: 0 },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: {
      categories: [
        "Planned Dealer",
        "Unplanned Dealer",
        "Prospect",
        "Customer",
        "Influencer",
      ],
    },
  });
  const categorySeries = [
    {
      name: "Visits",
      data: [
        totals.planned,
        totals.unplanned,
        totals.prospect,
        totals.customer,
        totals.influencer,
      ],
    },
  ];

  return (
    <>
      <PageHeader
        title="Visit Stats"
        subtitle="Field visit performance by representative"
        action={
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={generateExcel}
          >
            Download
          </Button>
        }
      />

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Field Reps"
                value={visitData.length}
                icon={PeopleAltOutlinedIcon}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Planned Visits"
                value={totals.planned}
                icon={EventAvailableOutlinedIcon}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Unplanned Visits"
                value={totals.unplanned}
                icon={EventBusyOutlinedIcon}
                color="warning"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Total Visits"
                value={totalVisits}
                icon={PlaceOutlinedIcon}
                color="info"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} md={5}>
              <SectionCard title="Visit Type Breakdown">
                {totalVisits > 0 ? (
                  <Chart
                    type="donut"
                    series={breakdownSeries}
                    options={breakdownOptions}
                    height={320}
                  />
                ) : (
                  <EmptyState
                    title="No visits recorded"
                    description="Visit type breakdown will appear once data is available."
                  />
                )}
              </SectionCard>
            </Grid>
            <Grid item xs={12} md={7}>
              <SectionCard title="Visits by Category">
                <Chart
                  type="bar"
                  series={categorySeries}
                  options={categoryOptions}
                  height={320}
                />
              </SectionCard>
            </Grid>
          </Grid>

          <SectionCard title="Representative Stats">
            <Box sx={{ mb: 2 }}>
              <TextField
                size="small"
                onChange={handleChange}
                value={search}
                name="search"
                label="SAP ID or Territory"
                sx={{ width: { xs: "100%", sm: 360 } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <DataGrid
              rows={search ? searchData : visitData}
              columns={columns}
              autoHeight={true}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              onRowClick={(data) => history.push(`/SO/${data.row.SAP_ID}/visits`)}
              className="TableCell"
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ fontSize: "0.75rem" }}
            />
          </SectionCard>
        </>
      )}
      <Toaster />
    </>
  );
};

const VisitStats = () => {
  return (
    <AccessController roles={["AO", "RO", "ZO", "ADMIN"]}>
      <DashGridL2 Top={TopBar} Main={Main} />
      <Toaster />
    </AccessController>
  );
};

export default VisitStats;
